/**
 * A 360° photo's signed variants (ADR-041): the KTX2 cube the headset uploads
 * as it is, and the light JPEG for thumbnails. The API signs them beside a
 * lesson's `media` (`variants`, keyed by media id); the headset and the
 * preview read them with `panoVariantsOf`. Only photos whose job is `ready`
 * have an entry; missing reads as `{}`.
 */
import { MEDIA_ID } from './media-ids.js';
/** The cube formats the media worker makes. A reader keeps others: the headset decides. */
export const PANO_CUBE_FORMATS = ['astc6x6'];
const SHA256 = /^[0-9a-f]{64}$/;
const FORMAT = /^[a-z0-9]{1,32}$/;
const FACE_MIN = 16;
const FACE_MAX = 4096;
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isCount = (value) => Number.isSafeInteger(value) && value >= 0;
/**
 * The variant's own signed route, same-origin: `/v1/media/<id>/<variant>`,
 * then nothing or its query. Never a URL to elsewhere, another photo's or
 * another variant's.
 */
const isRoute = (value, id, variant) => {
    if (typeof value !== 'string')
        return false;
    const path = `/v1/media/${id}/${variant}`;
    return value === path || value.startsWith(`${path}?`);
};
/** A signed file's URL, size and hash: all of the light variant, the cube's common part. */
function signedFile(value, id, variant) {
    if (!isRecord(value))
        return undefined;
    const { url, bytes, sha256 } = value;
    if (!isRoute(url, id, variant) || !isCount(bytes))
        return undefined;
    if (typeof sha256 !== 'string' || !SHA256.test(sha256))
        return undefined;
    return { url, bytes, sha256 };
}
function cubeOf(value, id) {
    const file = signedFile(value, id, 'cube');
    if (!file || !isRecord(value))
        return undefined;
    const { format, face } = value;
    if (typeof format !== 'string' || !FORMAT.test(format))
        return undefined;
    if (!isCount(face) || face < FACE_MIN || face > FACE_MAX)
        return undefined;
    return { url: file.url, format, face, bytes: file.bytes, sha256: file.sha256 };
}
/**
 * `variants` read leniently, like `mediaInfoOf`: an entry that can't be
 * trusted is dropped (that photo is shown from its JPEG), not fatal.
 */
export function panoVariantsOf(value) {
    const variants = {};
    if (!isRecord(value))
        return variants;
    for (const [id, entry] of Object.entries(value)) {
        if (!MEDIA_ID.test(id) || !isRecord(entry))
            continue;
        const cube = cubeOf(entry['cube'], id);
        const light = signedFile(entry['light'], id, 'light');
        if (cube && light)
            variants[id] = { cube, light };
    }
    return variants;
}
//# sourceMappingURL=pano-variants.js.map